import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateRibDto,
  GetRibQueryDto,
  GetRibResponse,
  RibDto,
  UpdateRibDto,
} from "@/types/api/api-types";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { axiosApi } from "./axios";

const getRibs = async (payload?: GetRibQueryDto) => {
  const response = await axiosApi.get<GetRibResponse>(`/v1/rib/bo`, {
    params: payload,
  });
  return response.data;
};

export const useGetRibs = (payload?: GetRibQueryDto) => {
  return useQuery({
    queryKey: ["rib", "bo", payload],
    queryFn: () => getRibs(payload),
  });
};

const createRib = async (payload: CreateRibDto) => {
  const response = await axiosApi.post<RibDto>(`/v1/rib`, payload);
  return response.data;
};

export const useCreateRib = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boParameters");

  return useMutation({
    mutationFn: createRib,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rib"] });
      toast.success(t("ribApi.createSuccess"));
    },
    onError: () => {
      toast.error(t("ribApi.createError"));
    },
  });
};

const updateRib = async ({ id, payload }: { id: string; payload: UpdateRibDto }) => {
  const response = await axiosApi.patch<RibDto>(`/v1/rib/${id}`, payload);
  return response.data;
};

export const useUpdateRib = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boParameters");

  return useMutation({
    mutationFn: updateRib,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rib"] });
      toast.success(t("ribApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("ribApi.updateError"));
    },
  });
};

const deleteRib = async (id: string) => {
  const response = await axiosApi.delete<RibDto>(`/v1/rib/${id}`);
  return response.data;
};

export const useDeleteRib = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boParameters");

  return useMutation({
    mutationFn: deleteRib,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rib"] });
      toast.success(t("ribApi.deleteSuccess"));
    },
    onError: () => {
      toast.error(t("ribApi.deleteError"));
    },
  });
};
